import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronDown, ChevronRight, Plus, Users, GitMerge, Eye, Target, Award, BarChart2, LogOut, ShieldCheck, X, ClipboardList, Calendar, UserCog } from 'lucide-react';
import { onAuthStateChanged, signOut, reauthenticateWithCredential, EmailAuthProvider, updatePassword } from 'firebase/auth';
import { collection, onSnapshot, doc, getDoc, addDoc, deleteDoc, updateDoc, writeBatch, arrayUnion, arrayRemove } from 'firebase/firestore';
import { auth, db } from './config/firebase';
import { classNames, compareObjectiveCodes, getCurrentFinancialYear, krHasIkrOwner, nextKrCode, reservedKrCodes } from './lib/utils';
import { createEmployee, deleteEmployeeCompletely, queueAuthCleanup, withdrawAuthCleanup, updateEmployeeManager } from './lib/employeeAdmin';
import StrategyView from './views/StrategyView';
import TrackingView from './views/TrackingView';
import TrackingViewFY2627 from './views/TrackingViewFY2627';
import EvaluationsView from './views/EvaluationsView';
import EvaluationsSummaryView from './views/EvaluationsSummaryView';
import InsightsView from './views/InsightsView';
import EmployeeManagementView from './views/EmployeeManagementView';
import LoginPage from './views/LoginPage';
import PermissionErrorScreen from './components/PermissionErrorScreen';

const App = () => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [permissionError, setPermissionError] = useState(null);
    const [okrs, setOkrs] = useState([]);
    const [teams, setTeams] = useState({});
    const [users, setUsers] = useState([]);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [activeTab, setActiveTab] = useState('tracking');
    const [expandedTeams, setExpandedTeams] = useState({});
    
    // FY state — starts on whichever financial year today falls in.
    const [activeFY, setActiveFY] = useState(() => getCurrentFinancialYear());
    const [okrsFY2627, setOkrsFY2627] = useState([]);
    const [selectedObjective, setSelectedObjective] = useState(null);
    
    // Handle URL parameters for deep linking (e.g., from email notifications)
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const tabParam = urlParams.get('tab');
        const periodParam = urlParams.get('period');
        const employeeParam = urlParams.get('employee');
        const fyParam = urlParams.get('fy');
        
        if (tabParam && ['strategy', 'tracking', 'evaluations', 'eval-summary', 'employees', 'insights'].includes(tabParam)) {
            setActiveTab(tabParam);
        }
        
        if (fyParam === 'FY2627') {
            setActiveFY('FY2627');
            sessionStorage.setItem('evaluationFY', 'FY2627');
        }
        
        if (periodParam || employeeParam) {
            sessionStorage.setItem('evaluationPeriod', periodParam || '');
            sessionStorage.setItem('evaluationEmployee', employeeParam || '');
        }
    }, []);
    
    // Password change state
    const [showPasswordChange, setShowPasswordChange] = useState(false);
    const [passwordChangeData, setPasswordChangeData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
    const [passwordChangeError, setPasswordChangeError] = useState('');
    const [passwordChangeSuccess, setPasswordChangeSuccess] = useState('');

    const toggleTeamExpansion = (teamId) => {
        setExpandedTeams(prev => ({ ...prev, [teamId]: !prev[teamId] }));
    };

    const handleRetry = () => {
        setPermissionError(null);
        setLoading(true);
        onAuthStateChanged(auth, user => {
            if (!user) {
                setLoading(false);
            }
        });
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    const userDocRef = doc(db, 'users', user.uid);
                    const userDoc = await getDoc(userDocRef);
                    if (userDoc.exists()) {
                        setCurrentUser({ uid: user.uid, ...userDoc.data() });
                    } else {
                        console.error(`CRITICAL: User profile not found in Firestore for UID: ${user.uid}. Logging out.`);
                        setPermissionError(`Your user account exists for login, but your user profile document was not found in the database. Please contact your administrator.`);
                        await signOut(auth);
                        setCurrentUser(null);
                    }
                } catch (error) {
                    console.error("FirebaseError fetching user profile:", error);
                    setPermissionError(`Could not fetch your user profile. This is likely a Firestore security rule issue. Please ensure authenticated users have read access to their own document in the 'users' collection.`);
                    await signOut(auth);
                    setCurrentUser(null);
                }
            } else {
                setCurrentUser(null);
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!currentUser || permissionError) return;

        const handleError = (error, collectionName) => {
            console.error(`Firestore Permissions Error: Could not fetch '${collectionName}'.`, error);
            setPermissionError(`Could not load data for '${collectionName}'. Please check your Firestore security rules to ensure authenticated users have read access to this collection.`);
        };

        const teamsCollectionRef = collection(db, 'teams');
        const unsubscribeTeams = onSnapshot(teamsCollectionRef, (snapshot) => {
            const teamsData = {};
            snapshot.forEach(docSnap => { teamsData[docSnap.id] = docSnap.data(); });
            setTeams(teamsData);
        }, (error) => handleError(error, 'teams'));

        const usersCollectionRef = collection(db, 'users');
        const unsubscribeUsers = onSnapshot(usersCollectionRef, (snapshot) => {
            const usersData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setUsers(usersData);
        }, (error) => handleError(error, 'users'));

        const okrsCollectionRef = collection(db, 'okrs');
        const unsubscribeOkrs = onSnapshot(okrsCollectionRef, (snapshot) => {
            const okrsData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setOkrs(okrsData);
        }, (error) => handleError(error, 'okrs'));

        const okrsFY2627Ref = collection(db, 'okrs_fy2627');
        const unsubscribeOkrsFY2627 = onSnapshot(okrsFY2627Ref, (snapshot) => {
            const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setOkrsFY2627(data);
        }, (error) => handleError(error, 'okrs_fy2627'));

        return () => {
            unsubscribeTeams();
            unsubscribeUsers();
            unsubscribeOkrs();
            unsubscribeOkrsFY2627();
        };
    }, [currentUser, permissionError]);

    // Deep links from evaluation emails can request a financial year; the header
    // toggle owns FY for every tab, so it is applied here.
    useEffect(() => {
        if (sessionStorage.getItem('evaluationFY') === 'FY2627') {
            setActiveFY('FY2627');
            sessionStorage.removeItem('evaluationFY');
        }
    }, []);

    const handleUpdateOkr = async (okrId, updatedData) => {
        try {
            // Filter out undefined values to prevent Firebase errors
            const cleanData = Object.fromEntries(
                Object.entries(updatedData).filter(([key, value]) => value !== undefined)
            );
            
            const okrRef = doc(db, 'okrs', okrId);
            await updateDoc(okrRef, cleanData);
        } catch (error) {
            console.error("FirebaseError on write operation:", error);
            if (error.code === 'permission-denied') {
                setPermissionError("You do not have permission to edit objectives. This action is restricted to Admins. Please check your user role or update the Firestore security rules.");
            }
        }
    };

    const handleDeleteOkr = async (okrId) => {
        try {
            const okrRef = doc(db, 'okrs', okrId);
            await deleteDoc(okrRef);
        } catch (error) {
            console.error("FirebaseError on write operation:", error);
            if (error.code === 'permission-denied') {
                setPermissionError("You do not have permission to delete objectives. This action is restricted to Admins. Please check your user role or update the Firestore security rules.");
            }
        }
    };

    const addNewObjective = async () => {
        const newObjective = {
            objective: 'New Objective',
            primaryTeamId: selectedTeam,
            associatedTeamIds: selectedTeam ? [selectedTeam] : [],
            keyResults: []
        };
        try {
            await addDoc(collection(db, 'okrs'), newObjective);
        } catch (error) {
            console.error("FirebaseError on write operation:", error);
            if (error.code === 'permission-denied') {
                setPermissionError("You do not have permission to create new objectives. This action is restricted to Admins. Please check your user role or update the Firestore security rules.");
            }
        }
    };

    // Function to update user access when Individual KR assignment changes
    // This will be used when implementing the KR assignment integration
    // eslint-disable-next-line no-unused-vars
    const updateUserAccessOnKrAssignment = async (userId, objectiveId, krId, teamId) => {
        try {
            const userRef = doc(db, 'users', userId);
            const userDoc = await getDoc(userRef);
            
            if (!userDoc.exists()) {
                console.error('User document not found for access update');
                return;
            }
            
            const userData = userDoc.data();
            const currentAccess = userData.crossTeamAccess || [];
            
            // Check if access already exists
            const existingAccess = currentAccess.find(access => 
                access.objectiveId === objectiveId && access.krId === krId
            );
            
            if (!existingAccess) {
                const newAccess = {
                    teamId: teamId,
                    reason: 'individual_kr_owner',
                    objectiveId: objectiveId,
                    krId: krId,
                    assignedAt: new Date()
                };
                
                await updateDoc(userRef, {
                    crossTeamAccess: [...currentAccess, newAccess]
                });
            }
        } catch (error) {
            console.error('Error updating user access:', error);
        }
    };

    // FY 26-27 CRUD handlers
    const handleUpdateOkrFY2627 = async (okrId, updatedData) => {
        try {
            const cleanData = Object.fromEntries(
                Object.entries(updatedData).filter(([, value]) => value !== undefined)
            );
            await updateDoc(doc(db, 'okrs_fy2627', okrId), cleanData);
        } catch (error) {
            console.error("FirebaseError on FY2627 write:", error);
            if (error.code === 'permission-denied') {
                setPermissionError("You do not have permission to edit FY 26-27 objectives. This action is restricted to Admins.");
            }
        }
    };

    const handleDeleteOkrFY2627 = async (okrId) => {
        try {
            await deleteDoc(doc(db, 'okrs_fy2627', okrId));
        } catch (error) {
            console.error("FirebaseError on FY2627 delete:", error);
            if (error.code === 'permission-denied') {
                setPermissionError("You do not have permission to delete FY 26-27 objectives. This action is restricted to Admins.");
            }
        }
    };

    /** Next free objective code in the existing O1, O2, … sequence. */
    const nextObjectiveCodeFY2627 = () => {
        const highest = okrsFY2627.reduce((max, obj) => {
            const match = /^O(\d+)$/i.exec((obj.code || '').trim());
            return match ? Math.max(max, parseInt(match[1], 10)) : max;
        }, 0);
        return `O${highest + 1}`;
    };

    /**
     * Codes already spoken for across FY26-27 — live KR codes plus every code
     * still held in an `accessibleKRs` array. `users` is a live snapshot of the
     * whole collection, so the held half is complete.
     */
    const reservedKrCodesFY2627 = useMemo(
        () => reservedKrCodes(okrsFY2627, users),
        [okrsFY2627, users]
    );

    /**
     * Next free KR code inside one objective. Lives here rather than in the
     * card because only this level can see every objective and every user, and
     * a code is only safe to issue once it has been checked against both.
     */
    const nextKrCodeFY2627 = useCallback(
        (objective) => nextKrCode(objective, reservedKrCodesFY2627),
        [reservedKrCodesFY2627]
    );

    const addNewObjectiveFY2627 = async () => {
        try {
            const docRef = await addDoc(collection(db, 'okrs_fy2627'), {
                code: nextObjectiveCodeFY2627(),
                objective: 'New Objective',
                keyResults: []
            });
            // Jump straight to the new objective so it can be renamed on the spot.
            setActiveTab('tracking');
            setSelectedObjective(docRef.id);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error) {
            console.error("FirebaseError on FY2627 write:", error);
            if (error.code === 'permission-denied') {
                setPermissionError("You do not have permission to create FY 26-27 objectives. This action is restricted to Admins.");
            }
        }
    };

    const updateUserKRAccess = async (userId, krCode) => {
        // A blank code grants nothing and is a wildcard once stored: an empty
        // string matches every uncoded KR in the company. New KRs now always
        // carry a code, so this only ever fires on legacy data.
        const code = String(krCode || '').trim();
        if (!userId || !code) return;
        try {
            const userRef = doc(db, 'users', userId);
            const userSnap = await getDoc(userRef);
            if (!userSnap.exists()) return;
            const currentKRs = userSnap.data().accessibleKRs || [];
            if (!currentKRs.some(c => String(c || '').trim() === code)) {
                await updateDoc(userRef, { accessibleKRs: [...currentKRs, code] });
            }
        } catch (error) {
            console.error('Error updating user KR access:', error);
        }
    };

    /**
     * Does `userId` still own an FY26-27 IKR under any KR coded `krCode`? The edited
     * objective is judged on its pending keyResults, because the onSnapshot state is
     * still one round-trip behind the write we are about to make.
     */
    const stillOwnsKrCode = (userId, krCode, okrId, pendingKeyResults) => {
        if (!userId || !krCode) return true;              // nothing to revoke
        const code = String(krCode).trim();
        return okrsFY2627.some(obj => {
            const keyResults = obj.id === okrId ? (pendingKeyResults || []) : (obj.keyResults || []);
            return keyResults.some(kr => String(kr.code || '').trim() === code && krHasIkrOwner(kr, userId));
        });
    };

    /**
     * Move one IKR to a new owner. FY26-27 also transfers the KR-code grant in
     * `accessibleKRs`: the new owner always gains it, the old owner loses it only
     * once they own nothing else under that code. FY25-26 writes no grant at all —
     * that year's visibility comes from the team hierarchy plus ownerId, and its
     * key results carry no `code` to grant.
     *
     * All writes go in one batch so the OKR tree and the two user docs cannot drift
     * apart. `accessibleKRs` is client-side UI filtering only (firestore.rules never
     * reads it), so a missed revoke is stale visibility, not an escalation.
     *
     * @returns {Promise<{ok: boolean, message?: string}>} Never sets permissionError:
     *          one failed row must not replace the whole app with the error screen.
     */
    const handleReassignIkrOwner = async ({ variant, okrId, keyResults, previousOwnerId, newOwnerId, krCode }) => {
        try {
            const batch = writeBatch(db);
            batch.update(doc(db, variant === 'FY2627' ? 'okrs_fy2627' : 'okrs', okrId), { keyResults });

            const code = String(krCode || '').trim();
            if (variant === 'FY2627' && code) {
                // updateDoc on a missing doc throws and would abort the batch, and `users`
                // is a live snapshot of the whole collection, so this is a reliable check.
                const findUser = (id) => (id ? users.find(u => (u.uid || u.id) === id) : null);

                // arrayUnion is idempotent, so granting unconditionally also self-heals
                // a grant that an earlier assignment missed.
                if (findUser(newOwnerId)) {
                    batch.update(doc(db, 'users', newOwnerId), { accessibleKRs: arrayUnion(code) });
                }

                const previous = findUser(previousOwnerId);
                const heldCode = (previous?.accessibleKRs || []).some(c => String(c || '').trim() === code);
                if (previous && heldCode && !stillOwnsKrCode(previousOwnerId, code, okrId, keyResults)) {
                    // Removing '' too clears the stray blank entry some docs carry. Only
                    // ever on a doc we were already writing, and never any other code.
                    batch.update(doc(db, 'users', previousOwnerId), { accessibleKRs: arrayRemove(code, '') });
                }
            }

            await batch.commit();
            return { ok: true };
        } catch (error) {
            console.error('FirebaseError on IKR reassign:', error);
            return {
                ok: false,
                message: error.code === 'permission-denied'
                    ? 'You do not have permission to reassign this IKR.'
                    : 'Reassign failed — please try again.',
            };
        }
    };

    /**
     * Add a person from the Employee Management tab. The manager is resolved here,
     * where the live `users` array lives, so the profile can store `managerId` —
     * firestore.rules matches a manager by id, not by email.
     */
    const handleCreateEmployee = async (form) => {
        const managerDoc = form.managerEmail
            ? users.find(u => (u.email || '').toLowerCase() === form.managerEmail.trim().toLowerCase())
            : null;
        return createEmployee({ ...form, managerDoc });
    };

    /**
     * Set or clear a person's manager from the Employee Management tab.
     *
     * Resolved here rather than in the panel for the same reason as creation: the
     * live `users` array is what turns the chosen email into the manager's uid,
     * and `managerId` is what firestore.rules matches on.
     */
    const handleUpdateEmployeeManager = async (uid, managerEmail) => {
        const trimmed = (managerEmail || '').trim().toLowerCase();
        const newManagerDoc = trimmed
            ? users.find(u => (u.email || '').toLowerCase() === trimmed)
            : null;
        if (trimmed && !newManagerDoc) {
            return {
                ok: false,
                code: 'unknown-manager',
                message: 'No one in the database has that email address. Pick a name from the list.',
            };
        }
        return updateEmployeeManager({ uid, newManagerDoc, users });
    };

    /**
     * Remove a person's Firestore footprint. Their Firebase login cannot be deleted
     * from a browser, so it is queued first — before anything is destroyed, so an
     * interrupted delete still leaves the trail for scripts/deleteUsers.js.
     */
    const handleDeleteEmployee = async (user, onProgress) => {
        const queued = await queueAuthCleanup({
            uid: user.id,
            email: user.email,
            name: user.name,
            deletedBy: currentUser?.email,
        });
        const result = await deleteEmployeeCompletely({
            uid: user.id,
            viewerUid: currentUser.uid || currentUser.id,
            onProgress,
        });
        // The profile survived, so the person is still in the app — take the entry
        // back out rather than leave a banner telling an admin to delete their login.
        if (!result?.profileDeleted && queued?.id) {
            await withdrawAuthCleanup(queued.id);
        }
        return result;
    };

    // FY 26-27 access control
    const canUserAccessObjectiveFY2627 = useCallback((objective) => {
        if (!currentUser) return false;
        if (currentUser.role === 'Admin') return true;
        // Both sides are trimmed and blanks dropped, so a stray '' in either
        // one can never match and hand over every uncoded KR.
        const userKRs = new Set((currentUser.accessibleKRs || [])
            .map(c => String(c || '').trim()).filter(Boolean));
        // Owning an IKR is access on its own, mirroring the cross-team rule the
        // FY25-26 path applies via `primaryTeamId`. The grant in `accessibleKRs`
        // is a convenience, not the only route: it can be missing because the KR
        // was never coded, because a code was later cleared, or because an
        // assignment failed to write it — and in none of those cases should
        // someone lose sight of their own work.
        const viewerId = currentUser.uid || currentUser.id;
        return (objective.keyResults || []).some(kr => (
            userKRs.has(String(kr.code || '').trim()) || krHasIkrOwner(kr, viewerId)
        ));
    }, [currentUser]);

    // Everything the viewer may see, before the sidebar objective filter — used by the person search.
    const accessibleOkrsFY2627 = useMemo(
        () => okrsFY2627
            .filter(obj => canUserAccessObjectiveFY2627(obj))
            .sort(compareObjectiveCodes),
        [okrsFY2627, canUserAccessObjectiveFY2627]
    );

    const filteredOkrsFY2627 = useMemo(() => {
        if (selectedObjective === null) return accessibleOkrsFY2627;
        return accessibleOkrsFY2627.filter(obj => obj.id === selectedObjective);
    }, [accessibleOkrsFY2627, selectedObjective]);

    const handleLogout = async () => {
        await signOut(auth);
    };

    // Password change functions
    const handlePasswordChangeInput = (field, value) => {
        setPasswordChangeData(prev => ({ ...prev, [field]: value }));
        setPasswordChangeError('');
        setPasswordChangeSuccess('');
    };

    const validatePasswordChange = () => {
        const { currentPassword, newPassword, confirmPassword } = passwordChangeData;
        
        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordChangeError('All fields are required');
            return false;
        }
        
        if (newPassword.length < 6) {
            setPasswordChangeError('New password must be at least 6 characters long');
            return false;
        }
        
        if (newPassword !== confirmPassword) {
            setPasswordChangeError('New passwords do not match');
            return false;
        }
        
        if (currentPassword === newPassword) {
            setPasswordChangeError('New password must be different from current password');
            return false;
        }
        
        return true;
    };

    const handlePasswordChange = async () => {
        if (!validatePasswordChange()) return;
        
        setPasswordChangeLoading(true);
        setPasswordChangeError('');
        setPasswordChangeSuccess('');
        
        try {
            const { currentPassword, newPassword } = passwordChangeData;
            
            // Get the current Firebase Auth user
            const firebaseUser = auth.currentUser;
            if (!firebaseUser) {
                throw new Error('No authenticated user found');
            }
            
            // Re-authenticate user
            const credential = EmailAuthProvider.credential(
                firebaseUser.email, 
                currentPassword
            );
            await reauthenticateWithCredential(firebaseUser, credential);
            
            // Update password
            await updatePassword(firebaseUser, newPassword);
            
            // Reset form
            setPasswordChangeData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
            setShowPasswordChange(false);
            setPasswordChangeSuccess('Password changed successfully! An email notification has been sent.');
            
            // Clear success message after 5 seconds
            setTimeout(() => {
                setPasswordChangeSuccess('');
            }, 5000);
            
        } catch (error) {
            console.error('Password change error:', error);
            
            switch (error.code) {
                case 'auth/wrong-password':
                    setPasswordChangeError('Current password is incorrect');
                    break;
                case 'auth/weak-password':
                    setPasswordChangeError('New password is too weak');
                    break;
                case 'auth/requires-recent-login':
                    setPasswordChangeError('Please log out and log back in to change your password');
                    break;
                default:
                    setPasswordChangeError('Failed to change password. Please try again.');
            }
        } finally {
            setPasswordChangeLoading(false);
        }
    };

    const cancelPasswordChange = () => {
        setShowPasswordChange(false);
        setPasswordChangeData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        setPasswordChangeError('');
        setPasswordChangeSuccess('');
    };

    const renderTeam = (teamId, level = 0) => {
        const team = teams[teamId];
        if (!team) return null;
        
        const noArrowTeams = ["Management", "Operations", "People & HR"];
        const hasChildren = team.children && team.children.length > 0;
        const isNoArrowTeam = noArrowTeams.includes(team.name);
        const isExpanded = expandedTeams[teamId];

        return (
            <div key={teamId}>
                <div 
                    onClick={() => {
                        setSelectedTeam(teamId);
                        if (hasChildren && !isNoArrowTeam) toggleTeamExpansion(teamId);
                    }} 
                    className={classNames(
                        'flex items-center p-2 rounded-lg cursor-pointer my-1',
                        selectedTeam === teamId ? 'bg-blue-100 text-blue-800' : 'text-gray-700 hover:bg-gray-200'
                    )}
                    style={{ paddingLeft: `${level * 20 + 8}px` }}
                >
                    {hasChildren && !isNoArrowTeam ? (
                        isExpanded ? <ChevronDown size={16} className="mr-2 flex-shrink-0" /> : <ChevronRight size={16} className="mr-2 flex-shrink-0" />
                    ) : (
                        <GitMerge size={16} className="mr-2 flex-shrink-0 opacity-50" />
                    )}
                    <span className="font-medium text-sm">{team.name}</span>
                    {isCrossTeamAccess(teamId) && (
                        <span className="ml-2 px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded-full">
                            Cross-team
                        </span>
                    )}
                </div>
                {hasChildren && isExpanded && (
                    <div>
                        {team.children.map(childId => renderTeam(childId, level + 1))}
                    </div>
                )}
            </div>
        );
    };

    // Helper function to calculate which teams a user can access
    const calculateUserAccessibleTeams = useMemo(() => {
        if (!currentUser || !okrs || !teams) return new Set();
        
        // Admin can access all teams
        if (currentUser.role === 'Admin') {
            return new Set(Object.keys(teams));
        }
        
        const accessibleTeams = new Set();
        
        // Always add user's primary team
        if (currentUser.teamId) {
            const normalizedTeamId = currentUser.teamId.trim();
            accessibleTeams.add(normalizedTeamId);
        }
        
        // Add hierarchical team access based on role
        if (currentUser.teamId) {
            const normalizedTeamId = currentUser.teamId.trim();
            
            // L1 Team Lead: Can access all teams in their vertical
            if (currentUser.role === 'L1 Team Lead') {
                const userRoot = normalizedTeamId.split('.')[0];
                Object.keys(teams).forEach(teamId => {
                    const teamRoot = teamId.split('.')[0];
                    if (teamRoot === userRoot) {
                        accessibleTeams.add(teamId);
                    }
                });
            }
            // L2 Team Lead: Can access their team and parent teams
            else if (currentUser.role === 'L2 Team Lead') {
                // Add all parent teams of user's team
                const userTeamParts = normalizedTeamId.split('.');
                for (let i = 1; i < userTeamParts.length; i++) {
                    const parentTeamId = userTeamParts.slice(0, i).join('.');
                    accessibleTeams.add(parentTeamId);
                }
                // Add all child teams of user's team
                Object.keys(teams).forEach(teamId => {
                    if (teamId.startsWith(normalizedTeamId + '.')) {
                        accessibleTeams.add(teamId);
                    }
                });
            }
            // Members: Can access their team and parent teams
            else {
                // Add all parent teams of user's team
                const userTeamParts = normalizedTeamId.split('.');
                for (let i = 1; i < userTeamParts.length; i++) {
                    const parentTeamId = userTeamParts.slice(0, i).join('.');
                    accessibleTeams.add(parentTeamId);
                }
                // Add all child teams of user's team
                Object.keys(teams).forEach(teamId => {
                    if (teamId.startsWith(normalizedTeamId + '.')) {
                        accessibleTeams.add(teamId);
                    }
                });
            }
        }
        
        // Find teams where user is mentioned in Individual KRs (cross-team access)
        okrs.forEach(okr => {
            okr.keyResults?.forEach(kr => {
                kr.individualKRs?.forEach(ikr => {
                    if (ikr.ownerId === currentUser.uid) {
                        accessibleTeams.add(okr.primaryTeamId);
                    }
                });
            });
        });
        return accessibleTeams;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser?.teamId, currentUser?.role, currentUser?.uid, okrs, teams]);

    // Helper function to check if user can access a specific team
    const canUserAccessTeam = (teamId) => {
        if (!currentUser) return false;
        if (currentUser.role === 'Admin') return true;
        
        // Check if user has direct access to this team
        if (calculateUserAccessibleTeams.has(teamId)) return true;
        
        // Check if this team is a child of user's team (hierarchical access)
        if (currentUser.teamId) {
            const normalizedUserTeamId = currentUser.teamId.trim();
            if (teamId.startsWith(normalizedUserTeamId + '.')) return true;
        }
        
        // Check if user's team is a child of this team (parent access)
        if (currentUser.teamId) {
            const normalizedUserTeamId = currentUser.teamId.trim();
            if (normalizedUserTeamId.startsWith(teamId + '.')) return true;
        }
        
        return false;
    };

    // Helper function to check if user can access a specific OKR
    const canUserAccessOkr = useCallback((okr) => {
        if (!currentUser) return false;
        if (currentUser.role === 'Admin') return true;
        
        const normalizedUserTeamId = currentUser.teamId?.trim();
        
        // L1 Team Lead: Can access all OKRs in their vertical
        if (currentUser.role === 'L1 Team Lead') {
            const userRoot = normalizedUserTeamId?.split('.')[0];
            const okrRoot = okr.primaryTeamId?.split('.')[0];
            if (userRoot && okrRoot && userRoot === okrRoot) return true;
        }
        
        // L2 Team Lead: Can access OKRs in their team and parent teams
        if (currentUser.role === 'L2 Team Lead') {
            // Can access if it's their primary team (exact match)
            if (okr.primaryTeamId === normalizedUserTeamId) return true;
            
            // Can access if it's a child team of their primary team
            if (normalizedUserTeamId && okr.primaryTeamId && okr.primaryTeamId.startsWith(normalizedUserTeamId + '.')) return true;
            
            // Can access if their team is a child of the OKR's team
            if (normalizedUserTeamId && okr.primaryTeamId && normalizedUserTeamId.startsWith(okr.primaryTeamId + '.')) return true;
        }
        
        // Members: Can access their team and parent teams, plus cross-team access
        if (!currentUser.role || currentUser.role === 'Member' || 
            (currentUser.role !== 'Admin' && currentUser.role !== 'L1 Team Lead' && currentUser.role !== 'L2 Team Lead')) {
            
            // Can access if it's their primary team (exact match)
            if (okr.primaryTeamId === normalizedUserTeamId) return true;
            
            // Can access if it's a child team of their primary team
            if (normalizedUserTeamId && okr.primaryTeamId && okr.primaryTeamId.startsWith(normalizedUserTeamId + '.')) return true;
            
            // Can access if their team is a child of the OKR's team
            if (normalizedUserTeamId && okr.primaryTeamId && normalizedUserTeamId.startsWith(okr.primaryTeamId + '.')) return true;
        }
        
        // Can access if they're mentioned in any Individual KR (cross-team access)
        const isMentioned = okr.keyResults?.some(kr => 
            kr.individualKRs?.some(ikr => ikr.ownerId === currentUser.uid)
        );
        
        return isMentioned;
    }, [currentUser]);

    // Helper function to check if a team is cross-team access for the user
    const isCrossTeamAccess = (teamId) => {
        if (!currentUser || currentUser.role === 'Admin') return false;
        
        const normalizedUserTeamId = currentUser.teamId?.trim();
        
        // Not cross-team if it's the user's primary team
        if (normalizedUserTeamId === teamId) return false;
        
        // Not cross-team if it's a hierarchical relationship (parent/child)
        if (normalizedUserTeamId && teamId.startsWith(normalizedUserTeamId + '.')) return false;
        if (normalizedUserTeamId && normalizedUserTeamId.startsWith(teamId + '.')) return false;
        
        // It's cross-team if user has access but it's not their primary team or hierarchical
        return calculateUserAccessibleTeams.has(teamId);
    };

    // Everything the viewer may see, before the sidebar team filter — used by the person search.
    const accessibleOkrs = useMemo(() => okrs.filter(okr => canUserAccessOkr(okr)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [okrs, currentUser, canUserAccessOkr]);

    const filteredOkrs = useMemo(() => {
        // Filter based on selected team
        if (selectedTeam === null) {
            return accessibleOkrs.filter(okr => okr.primaryTeamId === null);
        }
        return accessibleOkrs.filter(okr => okr.associatedTeamIds && okr.associatedTeamIds.includes(selectedTeam));
    }, [accessibleOkrs, selectedTeam]);
    
    const TabButton = ({ tabName, icon, label }) => ( <button onClick={() => setActiveTab(tabName)} className={classNames('flex items-center px-4 py-2.5 text-sm font-medium rounded-lg w-full text-left transition-colors', activeTab === tabName ? 'bg-blue-600 text-white shadow' : 'text-gray-600 hover:bg-gray-100')}> {icon} <span className="ml-3">{label}</span> </button> );

    if (permissionError) return <PermissionErrorScreen error={permissionError} onRetry={handleRetry} />;
    if (loading) return <div className="min-h-screen flex items-center justify-center"><p>Loading...</p></div>;
    if (!currentUser) return <LoginPage onLoginAttempt={() => setPermissionError(null)} />;

    return (
        <div className="bg-gray-100 min-h-screen font-sans">
            <header className="bg-white shadow-sm sticky top-0 z-20">
                <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                     <div className="flex justify-between items-center flex-wrap gap-4">
                        <div>
                             <div className="flex items-center gap-3">
                                 <img 
                                     src="/Rusk Logo Thumbnail.png" 
                                     alt="Rusk Media Logo" 
                                     className="h-8 w-auto"
                                 />
                                 <h1 className="text-2xl font-bold text-gray-900">APEX</h1>
                             </div>
                             <p className="text-gray-500 mt-1">Welcome, {currentUser.name} ({currentUser.role})</p>
                             {currentUser.role !== 'Admin' && calculateUserAccessibleTeams.size > 1 && (
                                 <p className="text-xs text-blue-600 mt-1">
                                     Access to {calculateUserAccessibleTeams.size} team{calculateUserAccessibleTeams.size > 1 ? 's' : ''}
                                 </p>
                             )}
                        </div>
                         <div className="flex items-center gap-4">
                             <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                                 <button onClick={() => { setActiveFY('FY2526'); setSelectedObjective(null); }} className={classNames('px-3 py-1.5 text-xs font-semibold rounded-md transition-colors', activeFY === 'FY2526' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700')}>
                                     <Calendar size={14} className="inline mr-1 -mt-0.5" />FY 25-26
                                 </button>
                                 <button onClick={() => { setActiveFY('FY2627'); setSelectedTeam(null); }} className={classNames('px-3 py-1.5 text-xs font-semibold rounded-md transition-colors', activeFY === 'FY2627' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700')}>
                                     <Calendar size={14} className="inline mr-1 -mt-0.5" />FY 26-27
                                 </button>
                             </div>
                             {activeTab === 'tracking' && currentUser.role === 'Admin' && (
                                 <button onClick={activeFY === 'FY2627' ? addNewObjectiveFY2627 : addNewObjective} className="flex items-center bg-blue-600 text-white font-semibold px-4 py-2 rounded-lg shadow hover:bg-blue-700 transition-all">
                                     <Plus size={18} className="mr-2" /> New Objective
                                 </button>
                             )}
                             <button onClick={() => setShowPasswordChange(true)} className="flex items-center text-gray-600 hover:text-blue-600 font-medium px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors">
                                 <ShieldCheck size={18} className="mr-2"/>Change Password
                             </button>
                             <button onClick={handleLogout} className="flex items-center text-gray-600 hover:text-red-600 font-medium px-4 py-2 rounded-lg hover:bg-red-50 transition-colors"><LogOut size={18} className="mr-2"/>Logout</button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                 <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                    <aside className="lg:col-span-1">
                        <div className="bg-white p-4 rounded-xl shadow-lg sticky top-24">
                           <div className="space-y-2 mb-6">
                               <TabButton tabName="strategy" icon={<Eye size={20} />} label="Strategy Overview" />
                               <TabButton tabName="tracking" icon={<Target size={20} />} label="OKR Tracking" />
                               <TabButton tabName="evaluations" icon={<Award size={20} />} label="Evaluations" />
                               {currentUser.role === 'Admin' && (
                                   <TabButton tabName="eval-summary" icon={<ClipboardList size={20} />} label="Eval Summary" />
                               )}
                               {currentUser.role === 'Admin' && (
                                   <TabButton tabName="employees" icon={<UserCog size={20} />} label="Employee Management" />
                               )}
                               <TabButton tabName="insights" icon={<BarChart2 size={20} />} label="Insights" />
                           </div>
                           
                           {activeTab === 'tracking' && activeFY === 'FY2526' && (
                           <div>
                                <h2 className="text-lg font-semibold text-gray-800 mb-3 border-t pt-4 px-2">Filter by Team</h2>
                                <nav className="space-y-1">
                                    <div onClick={() => setSelectedTeam(null)} className={classNames('flex items-center p-2 rounded-lg cursor-pointer my-1 font-semibold', selectedTeam === null ? 'bg-blue-100 text-blue-800' : 'text-gray-700 hover:bg-gray-200')}>
                                     <Users size={16} className="mr-3 flex-shrink-0" /> Company Wide
                                    </div>
                                    {Object.keys(teams)
                                        .filter(id => teams[id] && !teams[id].parent)
                                        .filter(teamId => canUserAccessTeam(teamId))
                                        .map(teamId => renderTeam(teamId))}
                                 </nav>
                           </div>
                           )}
                           {activeTab === 'tracking' && activeFY === 'FY2627' && (
                           <div>
                                <h2 className="text-lg font-semibold text-gray-800 mb-3 border-t pt-4 px-2">Core Objectives</h2>
                                <nav className="space-y-1">
                                    <div onClick={() => setSelectedObjective(null)} className={classNames('flex items-center p-2 rounded-lg cursor-pointer my-1 font-semibold', selectedObjective === null ? 'bg-blue-100 text-blue-800' : 'text-gray-700 hover:bg-gray-200')}>
                                        <Target size={16} className="mr-3 flex-shrink-0" /> All Objectives
                                    </div>
                                    {accessibleOkrsFY2627
                                        .map(obj => (
                                            <div key={obj.id} onClick={() => setSelectedObjective(obj.id)} className={classNames('flex items-center p-2 rounded-lg cursor-pointer my-1', selectedObjective === obj.id ? 'bg-blue-100 text-blue-800' : 'text-gray-700 hover:bg-gray-200')}>
                                                <span className="text-xs font-mono font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded mr-2 flex-shrink-0">{obj.code}</span>
                                                <span className="font-medium text-sm truncate">{obj.objective}</span>
                                            </div>
                                        ))
                                    }
                                </nav>
                           </div>
                           )}
                        </div>
                    </aside>
                    
                    <div className="lg:col-span-3">
                        {activeTab === 'strategy' && <StrategyView activeFY={activeFY} />}
                        {activeTab === 'tracking' && activeFY === 'FY2526' && <TrackingView okrs={filteredOkrs} handleUpdateOkr={handleUpdateOkr} handleDeleteOkr={handleDeleteOkr} users={users} teams={teams} viewer={currentUser} selectedTeamId={selectedTeam} allOkrs={okrs} searchOkrs={accessibleOkrs} onReassignIkrOwner={handleReassignIkrOwner} />}
                        {activeTab === 'tracking' && activeFY === 'FY2627' && <TrackingViewFY2627 okrs={filteredOkrsFY2627} handleUpdateOkr={handleUpdateOkrFY2627} handleDeleteOkr={handleDeleteOkrFY2627} users={users} viewer={currentUser} onOwnerAssigned={updateUserKRAccess} nextKrCode={nextKrCodeFY2627} searchOkrs={accessibleOkrsFY2627} onReassignIkrOwner={handleReassignIkrOwner} />}
                        {activeTab === 'evaluations' && <EvaluationsView okrs={okrs} okrsFY2627={okrsFY2627} users={users} teams={teams} viewer={currentUser} db={db} activeFY={activeFY} />}
                        {activeTab === 'eval-summary' && currentUser.role === 'Admin' && <EvaluationsSummaryView users={users} teams={teams} db={db} activeFY={activeFY} viewer={currentUser} okrs={okrs} okrsFY2627={okrsFY2627} />}
                        {activeTab === 'employees' && currentUser.role === 'Admin' && <EmployeeManagementView users={users} teams={teams} okrsFY2627={okrsFY2627} viewer={currentUser} onCreateEmployee={handleCreateEmployee} onDeleteEmployee={handleDeleteEmployee} onUpdateManager={handleUpdateEmployeeManager} />}
                        {activeTab === 'insights' && <InsightsView />}
                    </div>
                </div>
            </main>
            
            {/* Password Change Modal */}
            {showPasswordChange && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-bold text-gray-900">Change Password</h2>
                                <button 
                                    onClick={cancelPasswordChange}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                            
                            {passwordChangeSuccess && (
                                <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                                    {passwordChangeSuccess}
                                </div>
                            )}
                            
                            {passwordChangeError && (
                                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                                    {passwordChangeError}
                                </div>
                            )}
                            
                            <form onSubmit={(e) => { e.preventDefault(); handlePasswordChange(); }} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Current Password
                                    </label>
                                    <input
                                        type="password"
                                        value={passwordChangeData.currentPassword}
                                        onChange={(e) => handlePasswordChangeInput('currentPassword', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Enter current password"
                                        required
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        New Password
                                    </label>
                                    <input
                                        type="password"
                                        value={passwordChangeData.newPassword}
                                        onChange={(e) => handlePasswordChangeInput('newPassword', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Enter new password (min 6 characters)"
                                        required
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Confirm New Password
                                    </label>
                                    <input
                                        type="password"
                                        value={passwordChangeData.confirmPassword}
                                        onChange={(e) => handlePasswordChangeInput('confirmPassword', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Confirm new password"
                                        required
                                    />
                                </div>
                                
                                <div className="flex gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={cancelPasswordChange}
                                        className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                        disabled={passwordChangeLoading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={passwordChangeLoading}
                                    >
                                        {passwordChangeLoading ? 'Changing...' : 'Change Password'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default App;
